/* Intelligence Plan screen (W1-C) — #/client/{slug}/plan, a client cockpit deep
 * surface like Wikipedia / Search / AI. Structure + content from the pinned
 * anatomy spec §4.4(4)/§5.2/§5.3; visual grammar (plan-banner · opp-card ·
 * strategy-layout · work-package · gap-card) from the design export's Strategy
 * screen. Lit conventions per views/wiki.ts. Tokens only (plan.css).
 *
 * Findings → opportunities → work packages, with an evidence trace and an
 * explicit research-gap list. Every field is either sourced from the loaded run
 * payload / the live WS ledger, or an honest absence — the plan invents no
 * horizon, no confidence, no contract-scope, no fake decision persistence.
 *
 * THE THREE HONESTY LOAD-BEARERS on this screen:
 *  1. Horizons (30/90/180) are NOT in the Opportunity contract → every card reads
 *     "no horizon assigned"; there is no horizon filter (a filter over a
 *     dimension the data lacks would be a lie).
 *  2. Decisions (accept/defer/dismiss) have NO backend store (verified — no
 *     decisions table). The controls render DISABLED with a "not wired" note;
 *     no client-side-only state is ever written, and accepting therefore cannot
 *     — and by anatomy §5.3 must not — auto-create a task.
 *  3. Work packages: in this data model each opportunity maps to exactly ONE
 *     derived ledger task (`opp:*`, seeded at run reconcile). The panel shows
 *     that real linked task (matched by stable key), honestly labelled; richer
 *     multi-candidate packages are not yet modelled and are not faked.
 */
import { html, nothing, type TemplateResult } from 'lit';
import type { AppPayloadState } from '../../derive/state-shape.ts';
import type { Workspace } from '../../state/workspace.ts';
import type { WsTask } from '../../types/workspace.ts';
import type { PlanCoverage, PlanOpportunity } from '../../derive/plan.ts';
import { planCoverage, planIdentity, planOpportunities } from '../../derive/plan.ts';
import { getDecisions, setDecision, type Decision, type DecisionVerb } from '../../api/decisions.ts';

/** "Jul 17" from an ISO stamp; empty on a bad/absent value (never a fake date). */
function decDate(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d.getTime())
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
}

export interface PlanDeps {
  state: AppPayloadState;
  brand: string;
  slug: string;
  payloadLoaded: boolean;
  ws: Workspace;
  /** re-render the plan (opportunity selection). */
  rerender: () => void;
}

/* ── screen-local selection state (reset on teardown) ───────────────────────── */
let selectedRank = 1;

// W1-D: the real decisions store, keyed by opportunity key (opp:{action}:{slug}).
// Loaded once per client into this module and re-fetched after each write, so the
// plan renders the persisted decision (actor + date) and survives re-runs.
let decisionsByKey = new Map<string, Decision>();
let decisionsSlug: string | null = null;
let decisionsLoading = false;

/** Reset the plan's transient UI state — called by the cockpit on teardown /
 * client switch so a selection never survives into another client's plan. */
export function resetPlan(): void {
  selectedRank = 1;
  decisionsByKey = new Map();
  decisionsSlug = null;
}

async function loadDecisions(slug: string, rerender: () => void): Promise<void> {
  decisionsLoading = true;
  try {
    const rows = await getDecisions(slug);
    if (decisionsSlug !== slug) return; // client switched mid-flight — discard
    decisionsByKey = new Map(rows.map((d) => [d.opportunity_key, d]));
  } catch {
    decisionsByKey = new Map(); // degrade to honest empty, never a fabricated state
  } finally {
    decisionsLoading = false;
    rerender();
  }
}

async function decide(
  slug: string,
  key: string,
  verb: DecisionVerb,
  rerender: () => void,
): Promise<void> {
  try {
    const d = await setDecision(slug, key, verb);
    decisionsByKey.set(key, d);
  } catch {
    /* surfaced by the api layer; leave prior state intact */
  }
  rerender();
}

/* ── context bar (plan-specific — NOT a reputation surface tab) ─────────────── */
function contextBar(brand: string, slug: string): TemplateResult {
  return html`<div class="ww-context">
    <a class="ww-back" href=${`#/client/${slug}`}>← ${brand}</a>
    <div class="ww-crumbs"><b>${brand}</b> · Intelligence · <b>Intelligence Plan</b></div>
  </div>`;
}

function noResearch(brand: string, slug: string): TemplateResult {
  return html`<div class="wikiwork">${contextBar(brand, slug)}
    <div class="ww-content"><div class="ww-card"><p class="ww-empty">No research run for this client yet — the
      Intelligence Plan (evidence-backed opportunities across 30/90/180-day horizons) is generated once the
      first run lands.</p></div></div></div>`;
}

/* ── plan banner — real coverage state + honest gaps ───────────────────────── */
const CHIP_LABEL: Record<PlanCoverage['chip'], string> = {
  ready: 'Ready', partial: 'Partial', unknown: 'Coverage not phase-keyed',
};

function planBanner(cov: PlanCoverage, oppCount: number): TemplateResult {
  const gapLine = cov.gaps.length
    ? `${cov.gaps.length} explicit research gap${cov.gaps.length === 1 ? '' : 's'} — ${cov.gaps.map((g) => g.label).join(', ')}`
    : 'no research gaps flagged this run';
  return html`<div class="plan-banner">
    <span class="coverage-chip ${cov.chip}">${CHIP_LABEL[cov.chip]}</span>
    <div class="plan-banner-cov">
      <strong>Coverage: ${cov.headline}</strong>
      <small>${gapLine}</small>
    </div>
    <div class="plan-banner-plan">
      <strong>${oppCount} opportunit${oppCount === 1 ? 'y' : 'ies'} in this plan</strong>
      <small>ranked by research priority · decisions persist across re-runs — a re-run never resets them</small>
    </div>
  </div>`;
}

/* ── linked-work resolution (real WS ledger) ───────────────────────────────── */
interface LinkedWork {
  /** the matching ledger task, or null. */
  task: WsTask | null;
  /** honest one-line status for the work-package + card meta. */
  label: string;
  /** class for the status pill. */
  cls: string;
}

function resolveLinkedWork(ws: Workspace, o: PlanOpportunity): LinkedWork {
  if (ws.available !== true) {
    return { task: null, label: 'workspace offline — task link unavailable', cls: 'off' };
  }
  const key = o.foldedIntoWorksheet ? 'wikidata-worksheet' : o.actionKey;
  const task = ws.tasks.find((t) => t.action_key === key) || null;
  if (task) {
    const st = ws.STATUS_LABEL[task.status] || task.status;
    const via = o.foldedIntoWorksheet ? ' · via the Wikidata worksheet' : '';
    return { task, label: `task exists · linked · ${st}${via}`, cls: `linked s-${task.status}` };
  }
  return { task: null, label: 'candidate — not yet in the ledger', cls: 'candidate' };
}

/* ── one opportunity card ──────────────────────────────────────────────────── */
const DECISION_LABEL: Record<DecisionVerb, string> = {
  accepted: 'Accepted',
  deferred: 'Deferred',
  dismissed: 'Dismissed',
};

function oppCard(
  o: PlanOpportunity,
  ws: Workspace,
  slug: string,
  rerender: () => void,
): TemplateResult {
  const selected = o.rank === selectedRank;
  const work = resolveLinkedWork(ws, o);
  const meta: TemplateResult[] = [];
  meta.push(o.evidence.length
    ? html`<span>${o.evidence.length} evidence link${o.evidence.length === 1 ? '' : 's'}</span>`
    : html`<span class="hub-muted">no evidence links recorded</span>`);
  if (o.peersPresent.length) meta.push(html`<span>peers: ${o.peersPresent.join(', ')}</span>`);
  meta.push(html`<span class="opp-worklink ${work.cls}">${work.label}</span>`);

  // W1-D: the persisted decision for this opportunity (keyed on o.actionKey).
  const d = decisionsByKey.get(o.actionKey);
  const stateChip = d
    ? html`<span class="decide-state decided d-${d.decision}"
        >${DECISION_LABEL[d.decision]} · ${decDate(d.decided_at)}${d.actor ? html` · ${d.actor}` : nothing}</span>`
    : html`<span class="decide-state hub-muted">No decision recorded</span>`;
  const btn = (verb: DecisionVerb, label: string) => html`<button
    class=${d?.decision === verb ? 'on' : ''} aria-pressed=${d?.decision === verb ? 'true' : 'false'}
    @click=${(ev: Event) => { ev.stopPropagation(); void decide(slug, o.actionKey, verb, rerender); }}
  >${label}</button>`;

  return html`<article class=${`opp-card${selected ? ' selected' : ''}`} data-opp=${o.rank}
    data-oppkey=${o.actionKey}
    @click=${() => { selectedRank = o.rank; rerender(); }}>
    <div class="opp-rank">${o.rankLabel}</div>
    <div class="opp-body">
      <div class="opp-top">
        ${stateChip}
        <span class=${`plan-prio p-${o.priority}`}>${o.priority} priority</span>
        <span class="horizon-tag hub-muted" title="the run payload assigns no 30/90/180 horizon">no horizon assigned</span>
      </div>
      <h3>${o.title}</h3>
      <p>${o.rationale || html`<span class="hub-muted">No rationale recorded on this opportunity.</span>`}</p>
      <div class="opp-meta">${meta}</div>
      <div class="opp-decide" aria-label="opportunity decision">
        ${btn('accepted', 'Accept')}${btn('deferred', 'Defer')}${btn('dismissed', 'Dismiss')}
        <span class="opp-decide-note hub-muted">a decision is recorded with your name + the date and survives every
          re-run; accepting never creates a task — change it any time</span>
      </div>
    </div>
  </article>`;
}

/* ── work-package side panel (real linked task for the selected opp) ────────── */
function workPackage(o: PlanOpportunity | null, slug: string, ws: Workspace): TemplateResult {
  if (!o) {
    return html`<section class="wp-card"><div class="rail-panel-head"><span class="eyebrow">Work package</span></div>
      <p class="hub-muted">Select an opportunity to see its linked work.</p></section>`;
  }
  const work = resolveLinkedWork(ws, o);
  const boardHref = `#/client/${slug}/board`;
  let row: TemplateResult;
  if (work.task) {
    row = html`<a class="candidate linked" href=${boardHref}>
      <span class="wp-check" aria-hidden="true">✓</span>
      <span><strong>${o.title}</strong><small>${work.label} · #${work.task.id} — open on the board →</small></span>
    </a>`;
  } else if (work.cls === 'off') {
    row = html`<div class="candidate"><span class="wp-check off" aria-hidden="true">·</span>
      <span><strong>${o.title}</strong><small>${work.label}</small></span></div>`;
  } else {
    row = html`<div class="candidate"><span class="wp-check off" aria-hidden="true">·</span>
      <span><strong>${o.title}</strong><small>${work.label} — the derived task is seeded when this run reconciles
        into the ledger</small></span></div>`;
  }
  const evidence = o.evidence.length
    ? html`<div class="wp-evidence"><span class="eyebrow">Evidence trace</span>
        <ul>${o.evidence.map((e) => html`<li><a href=${e.url} target="_blank" rel="noopener">${e.label}</a></li>`)}</ul></div>`
    : html`<div class="wp-evidence"><span class="eyebrow">Evidence trace</span>
        <p class="hub-muted">No mapped-asset receipts for this platform on this run.</p></div>`;
  return html`<section class="wp-card">
    <div class="rail-panel-head"><span class="eyebrow">Work package · ${o.rankLabel}</span><span class=${`wp-status ${work.cls}`}>${work.task ? 'linked' : 'candidate'}</span></div>
    <h3>${o.title}</h3>
    ${row}
    ${evidence}
    <p class="wp-model-note hub-muted">In this data model each opportunity maps to a single derived work item —
      accepting an opportunity records strategic intent only and never auto-creates a task (anatomy §5.3);
      richer multi-step work packages are not yet modelled here.</p>
  </section>`;
}

/* ── research-gaps card (real, from coverage) ──────────────────────────────── */
function gapCard(cov: PlanCoverage): TemplateResult {
  return html`<section class="gap-card">
    <div class="rail-panel-head"><span class="eyebrow">Research gaps</span><span class="wire-tag">from this run's coverage</span></div>
    ${cov.gaps.length
      ? html`<ul>${cov.gaps.map((g) => html`<li><strong>${g.label}</strong> — ${g.note}</li>`)}</ul>`
      : html`<p class="hub-muted">No layer was flagged not-run or thin on this run.</p>`}
    ${!cov.available ? html`<p class="hub-muted gap-note">Per-layer coverage counts are not in the reconciled
      phase-keyed form on this run — the gaps above are read from the raw coverage notes.</p>` : nothing}
  </section>`;
}

/* ── compose ───────────────────────────────────────────────────────────────── */
export function renderPlanPage(deps: PlanDeps): TemplateResult {
  const { state, brand, slug, payloadLoaded, ws, rerender } = deps;
  if (!payloadLoaded) return noResearch(brand, slug);

  const ident = planIdentity(state);
  const cov = planCoverage(state);
  const opps = planOpportunities(state);
  const selected = opps.find((o) => o.rank === selectedRank) || opps[0] || null;

  // W1-D: load this client's decisions once (and on client switch), then render.
  if (decisionsSlug !== slug && !decisionsLoading) {
    decisionsSlug = slug;
    decisionsByKey = new Map();
    void loadDecisions(slug, rerender);
  }

  const eyebrow = [
    'Intelligence Plan',
    ident.dateLabel ? `generated ${ident.dateLabel}` : 'generation date not stamped',
    'version not tracked in this run',
  ].join(' · ');

  const body = opps.length
    ? html`<div class="strategy-layout">
        <div class="opp-list">${opps.map((o) => oppCard(o, ws, slug, rerender))}</div>
        <aside class="strategy-side" aria-label="selected opportunity work package">
          ${workPackage(selected, slug, ws)}
          ${gapCard(cov)}
        </aside>
      </div>`
    : html`<div class="ww-card"><p class="ww-empty">No opportunities were generated on this run — the plan is
        honestly empty. Opportunities appear once the research run surfaces evidence-backed potential actions.</p>
      ${gapCard(cov)}</div>`;

  return html`<div class="wikiwork plan-surface">
    ${contextBar(brand, slug)}
    <div class="ww-content">
      <header class="workroom-head plan-head">
        <div>
          <span class="dp-eyebrow">${eyebrow}</span>
          <h1 class="dp-h1">Intelligence Plan</h1>
          <p class="dp-lede">Evidence-backed opportunities with their real linked work. Decisions are designed to
            survive every future run — a re-run proposes changes, it never silently resets an accepted, deferred or
            dismissed opportunity.</p>
          ${ident.scopeNote ? html`<span class="plan-scopenote hub-muted">${ident.scopeNote}</span>` : nothing}
        </div>
      </header>
      ${planBanner(cov, opps.length)}
      ${body}
      <footer class="dp-footer">Opportunities, rationale and evidence receipts are the research run's own output;
        priority is research-asserted, not a daily to-do ranking. Horizons (30/90/180) and per-opportunity
        confidence are not carried by this run's contract and are shown as honestly absent. Linked work is the live
        AM ledger, matched by each opportunity's stable key. ${ident.dateLabel ? `Run generated ${ident.dateLabel}.` : ''}</footer>
    </div>
  </div>`;
}
